import { useCallback, useMemo } from 'react'
import { RootState } from 'app/store'
import DataKeys from 'k8s/DataKeys'
import useServerSidePagination from 'core/hooks/useServerSidePagination'
import { getPortsCount, listPortsNextPage } from '../actions'
import { portsSelector } from '../selectors'

interface UsePortsPaginationConfig {
  networkId: string
  /** Default sort key for initial load (default: 'created_at') */
  defaultSortKey?: string
  /** Default sort direction for initial load (default: 'desc') */
  defaultSortDir?: 'asc' | 'desc'
}

/**
 * Ports-specific pagination hook.
 *
 * This is a thin wrapper around the generic useServerSidePagination hook
 * that provides ports-specific configuration.
 */
export function usePortsPagination({
  networkId,
  defaultSortKey = 'created_at',
  defaultSortDir = 'desc',
}: UsePortsPaginationConfig) {
  // Create a selector that filters ports by network ID
  const networkPortsSelector = useMemo(() => {
    return (state: RootState) => {
      const ports = portsSelector(state)
      if (!ports || !Array.isArray(ports)) return []
      return ports.filter((port: any) => port.network_id === networkId)
    }
  }, [networkId])

  // Memoize fetch action to prevent unnecessary re-renders
  const fetchAction = useCallback(
    async (params: {
      limit: number
      marker?: string | null
      sortKey?: string
      sortDir?: 'asc' | 'desc'
      searchField?: string
      searchValue?: string
      filters?: Record<string, string>
    }) => {
      // Call the action - it returns the fetched ports for THIS page only
      const ports = await listPortsNextPage.call({
        network_id: networkId,
        limit: params.limit,
        marker: params.marker || undefined,
        sort_key: params.sortKey,
        sort_dir: params.sortDir,
        search_field: params.searchField,
        search_value: params.searchValue,
        filters: params.filters,
      })

      // Return the fetched data (current page only) and next marker
      const lastItem = ports?.[ports.length - 1]
      return {
        data: ports || [],
        nextMarker: lastItem?.id,
      }
    },
    [networkId],
  )

  // Memoize count action - accepts params for filtered counts
  const countAction = useCallback(
    (params?: { searchField?: string; searchValue?: string; filters?: Record<string, string> }) =>
      getPortsCount(networkId, params),
    [networkId],
  )

  // Memoize dependencies array to prevent effect re-runs
  const dependencies = useMemo(() => [networkId], [networkId])

  // Use the generic pagination hook with ports-specific config
  const paginationResult = useServerSidePagination({
    dataKey: DataKeys.OpenstackPorts,
    dataSelector: networkPortsSelector,
    fetchAction,
    countAction,
    uniqueIdentifier: 'id',
    entityKey: `OpenstackPorts_${networkId}`,
    dependencies,
    defaultSortKey,
    defaultSortDir,
  })

  return paginationResult
}

export default usePortsPagination
